import { makeAutoObservable } from 'mobx'
import { BUILD_VERSION, isProdEnv } from 'shared/config'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AutoRefreshStore } from './AutoRefreshStore'

export class IdleManagerStore {
  private _worker: SharedWorker | null = null
  private _expire_time = 0 // seconds
  private _autoRefreshStore: AutoRefreshStore

  constructor(private _entities: IEntitiesStore) {
    if (this._expire_time_second_debug) {
      this._expire_time = this._expire_time_second_debug
    } else {
      this._expire_time = 60 * 14
    }

    this._autoRefreshStore = new AutoRefreshStore(this, this._entities)

    makeAutoObservable(this)
  }

  init = async () => {
    await this._entities.organizationStore.initPromise

    const isHipaaCompliant = this._entities.organizationStore.isHipaaCompliant

    if (!isHipaaCompliant) return

    if (typeof SharedWorker === 'function') {
      if (!this._worker) {
        this._worker = new SharedWorker(`/shared_worker_idle.js?v=${BUILD_VERSION}`)

        this._worker.port.start()

        this._initLogic()
      }
    } else {
      console.warn('SharedWorker is not supported in this browser')
    }
  }

  dispose = () => {
    if (this._worker) {
      this._worker.port.postMessage({
        event: 'stop',
      })
      this._worker.port.postMessage({
        event: 'reset_time',
      })
      this._worker.port.close()
    }

    this._worker = null
    this._autoRefreshStore.dispose()
  }

  private _resetTime = () => {
    if (!this._worker) return

    this._worker.port.postMessage({
      event: 'reset_time',
    })
  }

  userIsActive = () => {
    this._resetTime()
    this._autoRefreshStore.userIsActive()
  }

  private _start = () => {
    if (!this._worker) return

    this._worker.port.postMessage({
      event: 'start',
    })
  }

  closeTab = () => {
    if (!this._worker) return

    this._worker.port.postMessage({
      event: 'close',
    })
  }

  private _initLogic = () => {
    if (!this._worker) return

    this._worker.port.postMessage({
      event: 'set_expire_time',
      value: this._expire_time,
    })

    this._start()
    this._autoRefreshStore.init()

    this._worker.port.onmessage = (e) => {
      if (e.data.event === 'init') {
        this._autoRefreshStore.checkAutoRefresh(e.data.data.time)
        this.debug('Event: init [IdleManagerStore]', e.data.data)
      }

      if (e.data.event === 'tick') {
        this._autoRefreshStore.checkAutoRefresh(e.data.data.time)
        this.debug('Event: tick [IdleManagerStore]', e.data.data)
      }

      if (e.data.event === 'expire_time') {
        window.sessionStorage.setItem('expire_time_session', '1')
        this._entities.authStore.logout()
        this.debug('Event: expire_time [IdleManagerStore]', e.data.data)
      }

      if (e.data.event === 'tab_close') {
        this.debug('Event: tab_close [IdleManagerStore]')
      }
    }
  }

  debug = (event: string, data?: unknown) => {
    if (isProdEnv) return

    if (uiStore.debugMode) {
      console.log(data, `${event} - [IdleManager]`)
    }
  }

  private get _expire_time_second_debug() {
    if (isProdEnv) return
    if (!uiStore.debugMode) return

    const query = Object.fromEntries(new URLSearchParams(location.search))
    const value = Number(query.expire_time_second)

    if (value > 0) return value

    return
  }

  get expire_time() {
    return this._expire_time
  }
}
